import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Stack,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { publicApi } from '../services/api';

const AccountDeletionConfirm: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [code, setCode] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setErrorMessage('Invalid confirmation link. Please check your email and try again, or request a new deletion link.');
    }
  }, [token]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setErrorMessage(null);
    setSuccessMessage(null);

    if (!token) {
      setErrorMessage('Invalid confirmation link. Please check your email and try again, or request a new deletion link.');
      return;
    }

    if (!code.trim()) {
      setErrorMessage('Please enter the confirmation code from your email.');
      return;
    }

    if (!confirmed) {
      setErrorMessage('Please confirm that you want to delete your account before proceeding.');
      return;
    }

    try {
      setSubmitting(true);
      await publicApi.confirmAccountDeletion({
        token,
        code: code.trim(),
      });

      setSuccessMessage(
        'Your account deletion request has been confirmed. Your account will be scheduled for deletion according to our data retention policies. Thank you for using SonEtch.'
      );
      
      // Clear form after successful submission
      setCode('');
      setConfirmed(false);
    } catch (error: any) {
      const errorMsg = error?.response?.data?.message || 
                       error?.message || 
                       'Something went wrong while confirming your deletion request. Please check your code and try again.';
      setErrorMessage(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          bgcolor: 'background.default',
          py: 6,
        }}
      >
        <Container maxWidth="sm">
          <Stack spacing={3} alignItems="center">
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <img
                src="/sonetch_logo.png"
                alt="SonEtch logo"
                style={{
                  maxWidth: '200px',
                  height: 'auto',
                  objectFit: 'contain',
                }}
              />
            </Box>
            <Card elevation={3} sx={{ width: '100%' }}>
              <CardContent>
                <Stack spacing={3} alignItems="center">
                  <Alert severity="error" sx={{ width: '100%' }}>
                    Invalid confirmation link. Please check your email and try again, or request a new deletion link.
                  </Alert>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/account-deletion-request')}
                  >
                    Request new deletion link
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Container>
      </Box>
    );
  }

  if (successMessage) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          bgcolor: 'background.default',
          py: 6,
        }}
      >
        <Container maxWidth="sm">
          <Stack spacing={3} alignItems="center">
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <img
                src="/sonetch_logo.png"
                alt="SonEtch logo"
                style={{
                  maxWidth: '200px',
                  height: 'auto',
                  objectFit: 'contain',
                }}
              />
            </Box>
            <Card elevation={3} sx={{ width: '100%' }}>
              <CardContent>
                <Stack spacing={3} alignItems="center">
                  <Alert severity="success" sx={{ width: '100%' }}>
                    {successMessage}
                  </Alert>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/loginPage')}
                  >
                    Return to login
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        bgcolor: 'background.default',
        py: 6,
      }}
    >
      <Container maxWidth="sm">
        <Stack spacing={3} alignItems="center">
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <img
              src="/sonetch_logo.png"
              alt="SonEtch logo"
              style={{
                maxWidth: '200px',
                height: 'auto',
                objectFit: 'contain',
              }}
            />
          </Box>
          <Card elevation={3} sx={{ width: '100%' }}>
            <CardContent>
              <Stack spacing={3} alignItems="center">
              <Typography variant="h4" component="h1" align="center">
                Confirm account deletion
              </Typography>
              <Typography variant="body1" align="center" color="text.secondary">
                You&apos;re almost done. Please enter the confirmation code from your email to complete the deletion of your SonEtch account.
              </Typography>
              <Typography variant="body2" align="center" color="text.secondary">
                <strong>Warning:</strong> This action cannot be undone. Once confirmed, your account will be scheduled for deletion and your data will be removed according to our data retention policies.
              </Typography>

              <Alert severity="warning" sx={{ width: '100%' }}>
                <Typography variant="body2">
                  <strong>Important:</strong> Any unused credits in your account will be lost and no refund will be given when your account is deleted.
                </Typography>
              </Alert>

              {errorMessage && (
                <Alert severity="error" sx={{ width: '100%' }}>
                  {errorMessage}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%', mt: 1 }}>
                <Stack spacing={2}>
                  <TextField
                    label="Confirmation code"
                    type="text"
                    fullWidth
                    required
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="Enter the code from your email"
                    autoComplete="off"
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={confirmed}
                        onChange={(e) => setConfirmed(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Typography variant="body2">
                        I understand that this action is permanent and I want to delete my account.
                      </Typography>
                    }
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    color="error"
                    disabled={submitting || !confirmed}
                    sx={{ mt: 1 }}
                    fullWidth
                  >
                    {submitting ? (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CircularProgress size={20} color="inherit" />
                        <span>Confirming deletion…</span>
                      </Stack>
                    ) : (
                      'Confirm account deletion'
                    )}
                  </Button>
                </Stack>
              </Box>
            </Stack>
          </CardContent>
          </Card>
        </Stack>
      </Container>
    </Box>
  );
};

export default AccountDeletionConfirm;

